import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  Bell, Check, CheckCheck, X, AlertTriangle, 
  Clock, UserPlus, FileText, Shield, Trash2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const TYPE_CONFIG = {
  task_due: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
  task_overdue: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  task_assigned: { icon: UserPlus, color: 'text-blue-600', bg: 'bg-blue-50' },
  compliance_deadline: { icon: Shield, color: 'text-purple-600', bg: 'bg-purple-50' },
  document_review: { icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  access_request: { icon: UserPlus, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  lead_stage: { icon: Clock, color: 'text-teal-600', bg: 'bg-teal-50' },
  system: { icon: Bell, color: 'text-gray-600', bg: 'bg-gray-50' }
};

export default function NotificationBell() {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    if (!token) return;
    const fetchCount = async () => {
      try {
        const res = await api.getUnreadNotificationCount(token);
        setUnreadCount(res.data.count);
      } catch (e) { /* silent */ }
    };
    fetchCount();
    // Also generate notifications on dashboard load
    api.generateNotifications(token).catch(() => {});
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Fetch notifications when panel opens
  useEffect(() => {
    if (!isOpen || !token) return;
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const res = await api.getNotifications(token, { limit: 20 });
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.unread);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    fetchNotifications();
  }, [isOpen, token]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const markAsRead = async (id) => {
    try {
      await api.markNotificationRead(token, id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.markAllNotificationsRead(token);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const deleteNotification = async (id) => {
    try {
      await api.deleteNotification(token, id);
      const wasUnread = notifications.find(n => n.id === id && !n.read);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-[#5D524D] hover:text-[#2C2420]"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Notification Panel */}
      {isOpen && (
        <div 
          className="absolute right-0 top-12 w-96 bg-white rounded-xl shadow-2xl border border-[#E6E0D9] z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E0D9] bg-[#FAFAF8]">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-sm text-[#2C2420]">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{unreadCount} new</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-[#4A3B32] hover:underline flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="ml-2 text-[#8C8078] hover:text-[#2C2420]">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto" data-testid="notification-list">
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-6 h-6 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-[#D4CCC6] mx-auto mb-2" />
                <p className="text-sm text-[#8C8078]">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const config = TYPE_CONFIG[notif.type] || TYPE_CONFIG.system;
                const Icon = config.icon;
                return (
                  <div
                    key={notif.id}
                    className={cn(
                      "flex items-start gap-3 px-4 py-3 border-b border-[#F5F3F0] hover:bg-[#FAFAF8] transition-colors group",
                      !notif.read && "bg-blue-50/30"
                    )}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", config.bg)}>
                      <Icon className={cn("w-4 h-4", config.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={cn(
                          "text-sm",
                          !notif.read ? "font-semibold text-[#2C2420]" : "text-[#5D524D]"
                        )}>
                          {notif.title}
                        </p>
                        {!notif.read && <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                      </div>
                      <p className="text-xs text-[#8C8078] mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-[#B5AEA7] mt-1">
                        {notif.createdAt ? formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true }) : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!notif.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }}
                          className="p-1 text-[#8C8078] hover:text-green-600 rounded"
                          title="Mark as read"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                        className="p-1 text-[#8C8078] hover:text-red-600 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
